import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import config from '../../config';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { useNotifications } from '../../context/NotificationContext';
import { useSubscription } from '../../hooks/useSubscription';
import chatService from '../../services/chat.service';
import { sortSympathyNotifications } from '../../utils/sympathyOrder';

// Две сердца — взаимная симпатия
const MutualIcon = ({ className = '' }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="24" height="24" rx="12" fill="#A1F69E" />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M5.44923 5.00798C6.28969 5.06888 7.16544 5.47858 8.00376 6.30776C8.84186 5.47881 9.71684 5.0699 10.5565 5.01035C11.5042 4.94314 12.3419 5.32616 12.9481 5.93045C14.1398 7.11857 14.514 9.26847 13.0353 10.7473C13.0303 10.7522 13.0252 10.7571 13.0199 10.7618L8.29147 15.0449C8.12817 15.1928 7.87934 15.1928 7.71604 15.0449L2.98754 10.7618C2.98232 10.7571 2.97721 10.7522 2.97222 10.7473C1.48574 9.26077 1.85797 7.11062 3.0536 5.92342C3.66122 5.32008 4.50069 4.93926 5.44923 5.00798Z"
      fill="#010D0D"
    />
    <path
      d="M12.4854 8.50977C13.3427 8.5719 14.1987 8.94279 15.0029 9.62891C15.8072 8.9426 16.6641 8.57252 17.5215 8.51172C18.6323 8.43304 19.6067 8.88435 20.3008 9.57617C21.643 10.9144 22.1044 13.3832 20.3887 15.0996L20.3896 15.1006L20.3555 15.1328L15.627 19.415V19.416C15.2733 19.736 14.7346 19.7359 14.3809 19.416V19.415L9.65234 15.1328C9.64078 15.1223 9.62975 15.1112 9.61914 15.1006C7.89607 13.3775 8.35275 10.9073 9.70117 9.56836C10.3973 8.87717 11.374 8.42924 12.4854 8.50977Z"
      fill="#010D0D"
      stroke="#A1F69E"
    />
  </svg>
);

// Синее сердце — суперлайк
const SuperLikeIcon = ({ className = '' }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <rect width="24" height="24" rx="12" fill="#84AFFF" />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M9.44923 7.00798C10.2897 7.06888 11.1654 7.47858 12.0038 8.30776C12.8419 7.47881 13.7168 7.0699 14.5565 7.01035C15.5042 6.94314 16.3419 7.32616 16.9481 7.93045C18.1398 9.11857 18.514 11.2685 17.0353 12.7473C17.0303 12.7522 17.0252 12.7571 17.0199 12.7618L12.2915 17.0449C12.1282 17.1928 11.8793 17.1928 11.716 17.0449L6.98754 12.7618C6.98232 12.7571 6.97721 12.7522 6.97222 12.7473C5.48574 11.2608 5.85797 9.11062 7.0536 7.92342C7.66122 7.32008 8.50069 6.93926 9.44923 7.00798Z"
      fill="#010D0D"
    />
  </svg>
);

// Зелёное сердце — обычный лайк
const LikeIcon = ({ className = '' }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="24" height="24" rx="12" fill="#A1F69E" />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M7.94923 5.00798C8.78969 5.06888 9.66544 5.47858 10.5038 6.30776C11.3419 5.47881 12.2168 5.0699 13.0565 5.01035C14.0042 4.94314 14.8419 5.32616 15.4481 5.93045C16.6398 7.11857 17.014 9.26847 15.5353 10.7473C15.5303 10.7522 15.5252 10.7571 15.5199 10.7618L10.7915 15.0449C10.6282 15.1928 10.3793 15.1928 10.216 15.0449L5.48754 10.7618C5.48232 10.7571 5.47721 10.7522 5.47222 10.7473C3.98574 9.26077 4.35797 7.11062 5.5536 5.92342C6.16122 5.32008 7.00069 4.93926 7.94923 5.00798Z"
      fill="#010D0D"
    />
  </svg>
);

// Аватар 48×48 с бейджем типа симпатии 24×24 слева вверху
function SympathyAvatar({ notification, blurred, onClick }) {
  const payload = notification?.payload || {};

  const badgeByType = {
    mutual: <MutualIcon className="size-[24px]" />,
    superlike: <SuperLikeIcon className="size-[14px]" />,
    like: <LikeIcon className="size-[14px]" />
  };

  return (
    <button
      type="button"
      onClick={() => onClick(notification)}
      className="relative size-[48px] shrink-0 cursor-pointer"
      aria-label={payload?.name || 'Симпатия'}
    >
      {payload?.photo ? (
        <img
          src={payload.photo}
          alt={payload?.name || ''}
          className={`size-[48px] rounded-full object-cover ${blurred ? 'blur-[6px]' : ''}`}
        />
      ) : (
        <div className="size-[48px] rounded-full bg-[#022424] border border-[#233636] flex items-center justify-center text-white/40 text-[18px]">
          ?
        </div>
      )}

      {/* Бейдж типа: два сердца (взаимная), синее сердце (суперлайк), зелёное (лайк) */}
      <div
        className={`absolute -top-[4px] -right-[4px] size-[24px] rounded-full flex items-center justify-center transition-all `}
      >
        {badgeByType[notification?.type] ?? <MutualIcon className="size-[24px]" />}
      </div>
    </button>
  );
}

// Лента симпатий над чатами: аватары недавних взаимных симпатий и суперлайков.
// Больше 4 — в конце ссылки «Все» на страницу /likes.
export function SympathyStrip() {
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const { isActiveSubscription } = useSubscription();
  const { updateCounts } = useNotifications();

  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    axiosPrivate
      .get(config.API.NOTIFICATIONS.LIST, {
        // СИМПАТИИ = только супер-лайки и взаимные симпатии;
        // обычные лайки живут отдельно на странице /likes
        params: { type: 'mutual,superlike', limit: 50 }
      })
      // История не чистится по факту просмотра: кружки пропадают только после
      // реального действия (лайк/дизлайк по суперлайку), поэтому seen не фильтруем
      .then((res) => setNotifications(res.data?.data?.items || []))
      .catch((error) => console.error('Error fetching sympathies:', error));
  }, []);

  // Порядок: взаимные → суперлайки → лайки, внутри групп новые первыми.
  // Новый человек встаёт в начало своей группы, сдвигая остальных.
  // Кружки не скрываются после просмотра или сообщения в чате;
  // у суперлайка прежнее правило — исчезает после лайка/дизлайка.
  const uniqueNotifications = useMemo(() => sortSympathyNotifications(notifications), [notifications]);

  const refreshCounts = async () => {
    try {
      const response = await axiosPrivate.get(config.API.NOTIFICATIONS.COUNTS);
      if (response.data?.status === 'success') {
        updateCounts(response.data.data);
      }
    } catch (err) {
      console.error('Error refreshing counts:', err);
    }
  };

  // Клик по аватару = просмотр: гасим подсветку и ведём в чат/профиль
  const handleAvatarClick = async (notification) => {
    if (!notification?._id) return;

    const payload = notification.payload || {};
    const userId = payload?._id;
    const isDeletedUser = Boolean(payload?.isDeleted);

    // Отмечаем прочитанными все уведомления этого пользователя
    const targets = notifications.filter((n) => !n.seen && n.payload?._id === userId);
    if (targets.length > 0) {
      try {
        await Promise.all(
          targets.map((n) =>
            axiosPrivate.post(config.API.NOTIFICATIONS.MARK_READ(n._id)).catch((err) => console.error(err))
          )
        );
        setNotifications((prev) => prev.map((n) => (n.payload?._id === userId ? { ...n, seen: true } : n)));
        refreshCounts();
      } catch (error) {
        console.error('Error marking notifications as read:', error);
      }
    }

    // Без подписки суперлайки скрыты — клик ведёт на пейвол (как на /sympathies)
    if (!isActiveSubscription && notification.type !== 'mutual') {
      navigate(config.ROUTES.PREMIUM);
      return;
    }

    if (!userId || isDeletedUser) {
      toast.error('Профиль пользователя больше недоступен');
      return;
    }

    if (notification.type === 'mutual') {
      try {
        const res = await chatService.getChatToUserId(axiosPrivate, userId);
        navigate(config.ROUTES.CHATS.TEXT(res.data._id), { state: { from: config.ROUTES.CHATS.LIST } });
      } catch (error) {
        const message = error?.response?.data?.message;
        console.error('[SympathyStrip] getChatToUserId:', error);
        toast.error(message || 'Ошибка при попытке открыть чат с пользователем');
      }
      return;
    }

    // superlike — профиль пользователя
    navigate(config.ROUTES.MATCHS.SUPER_LIKE_PROFILE_ID(userId, notification._id), {
      state: { from: config.ROUTES.CHATS.LIST }
    });
  };

  if (uniqueNotifications.length === 0) return null;

  const visible = uniqueNotifications.slice(0, 4);
  const hasMore = uniqueNotifications.length > 4;

  return (
    <div className="w-full p-4 pt-3">
      <p className="text-[14px] font-semibold text-white uppercase text-left">Симпатии</p>

      {/* Аватары и «Все» в одну линию */}
      <div className="flex items-center gap-[22px] mt-4">
        {visible.map((notification) => (
          <SympathyAvatar
            key={notification._id}
            notification={notification}
            blurred={!isActiveSubscription && notification.type !== 'mutual'}
            onClick={handleAvatarClick}
          />
        ))}

        {hasMore && (
          <button
            type="button"
            onClick={() => navigate(config.ROUTES.SYMPATHIES)}
            className="flex items-center gap-[6px] shrink-0 cursor-pointer ml-auto pr-[4px]"
          >
            <span className="font-raleway text-[18px] text-[#A1F69E] font-medium">Все</span>
            <svg width="14" height="11" viewBox="0 0 14 11" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M-7.53403e-05 5.4082H12.3076M7.30762 0.408203L12.3076 5.4082L7.30762 10.4082"
                stroke="#A1F69E"
                stroke-width="1.15385"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
